import { auth } from './firebase';
import { dbg, dbgErr } from './debug';

export class ApiError extends Error {
  constructor(status, message, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function authHeader() {
  const u = auth.currentUser;
  if (!u) return {};
  const token = await u.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function request(method, path, body) {
  const headers = {
    Accept: 'application/json',
    ...(await authHeader()),
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const url = path.startsWith('/') ? path : `/${path}`;
  const authed = 'Authorization' in headers;
  const started = performance.now();
  dbg('api', `→ ${method} ${url}${authed ? ' 🔒' : ''}`, body ?? '');

  let res;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    dbgErr('err', `${method} ${url} — network error`, networkErr);
    throw networkErr;
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;
  const ms = Math.round(performance.now() - started);

  if (!res.ok) {
    const msg =
      (data && typeof data === 'object' && 'error' in data
        ? String(data.error)
        : null) || res.statusText || `HTTP ${res.status}`;
    dbgErr('err', `${method} ${url} — ${res.status} ${msg} (${ms}ms)`, data);
    throw new ApiError(res.status, msg, data);
  }
  dbg('ok', `${method} ${url} — ${res.status} (${ms}ms)`, data);
  return data;
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body ?? {}),
  put: (path, body) => request('PUT', path, body ?? {}),
  patch: (path, body) => request('PATCH', path, body ?? {}),
  delete: (path) => request('DELETE', path),
};


/**
 * Download a file from an authenticated endpoint. A plain <a href=…> fails
 * because the browser does not attach our Firebase ID token, so the Cloud Run
 * backend sees no auth and redirects to /login. We fetch with the token,
 * stream to a blob, and trigger a download via a short-lived object URL.
 *
 * Returns a promise that resolves when the file download has been triggered
 * (the actual save-to-disk happens in the browser).
 */
export async function downloadFile(path, suggestedFilename) {
  const url = path.startsWith('/') ? path : `/${path}`;
  const headers = { ...(await authHeader()) };
  const started = performance.now();
  dbg('api', `↓ GET ${url} (download)`);

  const res = await fetch(url, { method: 'GET', headers, credentials: 'same-origin' });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const t = await res.text();
      const j = safeJson(t);
      if (j && typeof j === 'object' && 'error' in j) msg = String(j.error);
    } catch { /* ignore */ }
    dbgErr('err', `↓ GET ${url} failed — ${msg}`);
    throw new ApiError(res.status, msg, null);
  }

  // Figure out filename: explicit arg > Content-Disposition > URL tail
  let filename = suggestedFilename;
  if (!filename) {
    const cd = res.headers.get('Content-Disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    if (match && match[1]) {
      try {
        filename = decodeURIComponent(match[1]);
      } catch {
        filename = match[1];
      }
    }
  }
  if (!filename) {
    const tail = url.split('?')[0].split('/').filter(Boolean).pop() || 'download';
    filename = tail;
  }

  // Force the correct extension based on Content-Type. Some browsers'
  // Save-As dialogs silently strip the extension from the `download`
  // attribute when the mime is recognised (e.g. DOCX renders as
  // "OOXML Text Document" with no `.docx` in the filename field). We
  // re-append the canonical extension here so the saved file always has
  // one the OS recognises.
  const extByMime = {
    'application/pdf': '.pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.ms-excel': '.xls',
    'text/csv': '.csv',
    'application/zip': '.zip',
    'application/json': '.json',
  };
  const rawMime = (res.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase();
  const expectedExt = extByMime[rawMime];
  if (expectedExt && !filename.toLowerCase().endsWith(expectedExt)) {
    // Strip any trailing dot-extension first so we don't end up with
    // `file.txt.docx` for a file that had the wrong extension.
    filename = filename.replace(/\.[a-zA-Z0-9]{1,5}$/, '') + expectedExt;
  }

  // Re-wrap the downloaded bytes as `application/octet-stream` before creating
  // the blob URL. Chrome/Edge recognise the Office MIME types and, for those,
  // silently strip the extension from the <a download> attribute — the Save
  // As dialog then shows the filename without `.docx` and as a generic "OOXML
  // Text Document". Forcing octet-stream locally keeps the filename intact.
  const rawBlob = await res.blob();
  const blob = new Blob([rawBlob], { type: 'application/octet-stream' });
  const blobUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    // Some browsers need the node in the DOM for the click to register
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Give the browser a tick to start the download, then free the object URL
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
  dbg('ok', `↓ ${url} saved as ${filename} (${Math.round(performance.now() - started)}ms)`);
}
